/**
 * RegenerationModal Component
 * Modal for batch content regeneration with progress tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  DollarSign,
  Clock,
  FileText,
} from 'lucide-react';
import type { ContentType, RegenerationJob, RegenerationEstimate } from '@/hooks/useContentConfig';

interface RegenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryPath?: string;
  onEstimate: (contentTypes: ContentType[]) => Promise<RegenerationEstimate | null>;
  onStart: (contentTypes: ContentType[]) => Promise<RegenerationEstimate | null>;
  onGetStatus: (jobId: string) => Promise<RegenerationJob | null>;
  onCancel: (jobId: string) => Promise<void>;
}

const CONTENT_TYPE_OPTIONS: { value: ContentType; label: string; description: string }[] = [
  { value: 'category_aeo', label: 'Category AEO', description: 'Category landing page content' },
  { value: 'product_aeo', label: 'Product AEO', description: 'Product page structured content' },
  { value: 'faq', label: 'FAQs', description: 'Frequently asked questions' },
  { value: 'product_description', label: 'Product Descriptions', description: 'Product summaries' },
  { value: 'ai_article', label: 'AI Articles', description: 'Blog and article content' },
];

export function RegenerationModal({
  isOpen,
  onClose,
  categoryPath,
  onEstimate,
  onStart,
  onGetStatus,
  onCancel,
}: RegenerationModalProps) {
  const [selectedTypes, setSelectedTypes] = useState<ContentType[]>(['category_aeo', 'product_aeo', 'faq']);
  const [estimate, setEstimate] = useState<RegenerationEstimate | null>(null);
  const [job, setJob] = useState<RegenerationJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch estimate when selection changes
  useEffect(() => {
    if (isOpen && selectedTypes.length > 0 && !job) {
      const fetchEstimate = async () => {
        setLoading(true);
        setError(null);
        try {
          const result = await onEstimate(selectedTypes);
          setEstimate(result);
        } catch (err) {
          setError('Failed to estimate regeneration scope');
        } finally {
          setLoading(false);
        }
      };
      fetchEstimate();
    }
  }, [isOpen, selectedTypes, onEstimate, job]);

  // Poll job status
  useEffect(() => {
    if (!job || job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
      return;
    }

    const pollInterval = setInterval(async () => {
      const status = await onGetStatus(job.id);
      if (status) {
        setJob(status);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [job, onGetStatus]);

  const handleToggleType = (type: ContentType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await onStart(selectedTypes);
      if (result?.jobId) {
        const status = await onGetStatus(result.jobId);
        setJob(status);
      }
    } catch (err) {
      setError('Failed to start regeneration');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!job) return;
    try {
      await onCancel(job.id);
      const status = await onGetStatus(job.id);
      setJob(status);
    } catch (err) {
      setError('Failed to cancel job');
    }
  };

  const handleClose = useCallback(() => {
    setJob(null);
    setEstimate(null);
    setError(null);
    onClose();
  }, [onClose]);

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusIcon = () => {
    if (!job) return null;
    switch (job.status) {
      case 'running':
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-orange-500" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Regenerate Content
          </DialogTitle>
          <DialogDescription>
            {categoryPath
              ? `Regenerate content for "${categoryPath}" with updated configuration`
              : 'Regenerate all site content with updated configuration'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Content Type Selection (only show if no active job) */}
          {!job && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Content Types to Regenerate</Label>
              <div className="space-y-2">
                {CONTENT_TYPE_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start space-x-3">
                    <Checkbox
                      id={`regen-${opt.value}`}
                      checked={selectedTypes.includes(opt.value)}
                      onCheckedChange={() => handleToggleType(opt.value)}
                    />
                    <Label htmlFor={`regen-${opt.value}`} className="flex flex-col cursor-pointer">
                      <span className="font-medium">{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.description}</span>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Estimate */}
          {estimate && !job && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Items to regenerate:</span>
                </div>
                <Badge variant="secondary">{estimate.estimatedItems}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Estimated time:</span>
                </div>
                <Badge variant="secondary">{formatTime(estimate.estimatedSeconds)}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Estimated cost:</span>
                </div>
                <Badge variant="secondary">${estimate.estimatedCost.toFixed(2)}</Badge>
              </div>
            </div>
          )}

          {/* Job Progress */}
          {job && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon()}
                  <span className="font-medium capitalize">{job.status}</span>
                </div>
                <Badge variant={job.status === 'completed' ? 'default' : 'secondary'}>
                  {job.completedItems} / {job.totalItems}
                </Badge>
              </div>

              <Progress value={job.progress} className="h-2" />

              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="p-2 rounded-md bg-green-500/10">
                  <div className="font-medium text-green-600">{job.completedItems}</div>
                  <div className="text-xs text-muted-foreground">Completed</div>
                </div>
                <div className="p-2 rounded-md bg-red-500/10">
                  <div className="font-medium text-red-600">{job.failedItems}</div>
                  <div className="text-xs text-muted-foreground">Failed</div>
                </div>
                <div className="p-2 rounded-md bg-muted/50">
                  <div className="font-medium">${job.actualCost.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">Cost</div>
                </div>
              </div>

              {/* Errors */}
              {job.errors && job.errors.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded-md border p-2 text-xs">
                  {job.errors.slice(0, 5).map((err, i) => (
                    <div key={i} className="text-red-600">
                      {err.contentType}: {err.error}
                    </div>
                  ))}
                  {job.errors.length > 5 && (
                    <div className="text-muted-foreground">
                      +{job.errors.length - 5} more errors
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Warning */}
          {!job && estimate && estimate.estimatedItems > 100 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will regenerate {estimate.estimatedItems} items. The process cannot be undone,
                but previous versions will be saved for 7 days and can be rolled back.
              </AlertDescription>
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {!job ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleStart}
                disabled={loading || selectedTypes.length === 0}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Start Regeneration
                  </>
                )}
              </Button>
            </>
          ) : job.status === 'running' ? (
            <>
              <Button variant="outline" onClick={handleCancel}>
                Cancel Job
              </Button>
              <Button disabled>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
